import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import {
  ArrowDownUp,
  Activity,
  ClipboardCheck,
  History,
  Settings,
} from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { StockSyncOverview } from './StockSyncOverview';
import { ApprovalQueue } from './ApprovalQueue';
import { PushHistory } from './PushHistory';
import { StockSyncSettings } from './StockSyncSettings';
import { useStockSyncOverview } from '../hooks/useStockSync';

type Tab = 'dashboard' | 'approvals' | 'history' | 'configuration';

const TABS: readonly Tab[] = ['dashboard', 'approvals', 'history', 'configuration'];

/** Map old tab names to current values for bookmark compat */
const TAB_ALIASES: Record<string, Tab> = {
  overview: 'dashboard',
  settings: 'configuration',
  notifications: 'configuration',
};

function getActiveTab(searchParams: URLSearchParams): Tab {
  const raw = searchParams.get('tab');
  if (raw && TABS.includes(raw as Tab)) return raw as Tab;
  if (raw && raw in TAB_ALIASES) return TAB_ALIASES[raw]!;
  return 'dashboard';
}

export function StockSyncPage() {
  const { t } = useTranslation('inventory');
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = getActiveTab(searchParams);
  const { data: overview } = useStockSyncOverview();

  const pendingCount = overview?.pendingCount ?? 0;

  const handleTabChange = useCallback((tab: string) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', tab);
    setSearchParams(next);
  }, [searchParams, setSearchParams]);

  const navigateToApprovals = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', 'approvals');
    setSearchParams(next);
  }, [searchParams, setSearchParams]);

  return (
    <div className="w-full py-6 px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10">
            <ArrowDownUp className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">{t('stockSync.title')}</h1>
            <p className="text-sm text-muted-foreground">
              {t('stockSync.subtitle')}
            </p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList>
          <TabsTrigger value="dashboard" className="flex items-center gap-2">
            <Activity className="h-4 w-4" />
            {t('stockSync.tabs.dashboard', 'Dashboard')}
          </TabsTrigger>
          <TabsTrigger value="approvals" className="flex items-center gap-2">
            <ClipboardCheck className="h-4 w-4" />
            {t('stockSync.tabs.approvals')}
            {pendingCount > 0 && (
              <Badge variant="destructive" className="ml-1 h-5 min-w-[20px] px-1.5 text-xs">
                {pendingCount}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="history" className="flex items-center gap-2">
            <History className="h-4 w-4" />
            {t('stockSync.tabs.history')}
          </TabsTrigger>
          <TabsTrigger value="configuration" className="flex items-center gap-2">
            <Settings className="h-4 w-4" />
            {t('stockSync.tabs.configuration', 'Configuration')}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="dashboard" className="mt-6">
          <StockSyncOverview onNavigateApprovals={navigateToApprovals} />
        </TabsContent>

        <TabsContent value="approvals" className="mt-6">
          <ApprovalQueue />
        </TabsContent>

        <TabsContent value="history" className="mt-6">
          <PushHistory />
        </TabsContent>

        <TabsContent value="configuration" className="mt-6 max-w-3xl">
          <StockSyncSettings />
        </TabsContent>
      </Tabs>
    </div>
  );
}
